/** The in-app confirmation, replacing every `window.confirm` / `window.prompt` (2026-09-10).
 *
 * Why this exists: the Teacher Computer app is a Tauri shell, and its webview (wry) does not
 * implement WKWebView's `runJavaScriptConfirmPanel` / `...TextInputPanel` delegates. On macOS
 * a webview whose delegate does not answer that selector returns to the page immediately with
 * `false` / `null`, so in the desktop app every native dialog was silently dismissed: "End
 * session" did nothing at all, and nor did nine other destructive actions. A dialog we render
 * ourselves is the only kind that behaves the same in a browser, in the desktop shell and in
 * the mobile shells.
 *
 * The dialog mechanics themselves live in `ui/Sheet.tsx`, extracted from here on 2026-09-10
 * when the live view needed sheets of its own: portal, scrim, `role="dialog"` with
 * `aria-modal`, the app behind made `inert`, focus moved in and returned to the trigger, Tab
 * cycling, Escape cancelling. There is one modal in this app and this renders inside it.
 * What stays here is the question: the destructive button is never what focus lands on, so a
 * hurried Return keeps the data.
 *
 * Everything is drawn from the tokens: the sheet radius, the raised shadow, the control scale
 * through `Button` and `.field`, sizes in rem so the interface magnifier scales the dialog
 * with the rest of the app. Motion is the app's one state-change duration (150 ms) and the
 * global reduced-motion rule switches it off.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useId,
  type FormEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { Button } from "./Button";
import { Sheet } from "./Sheet";

export type ConfirmOptions = {
  /** The question, short. Sentence case, ending in a question mark. */
  title: string;
  /** What will happen. One or two sentences; omit it when the title says everything. */
  body?: string;
  /** Names the action ("End session", "Archive"), never "OK". */
  confirmLabel: string;
  /** Defaults to the shared "Cancel". */
  cancelLabel?: string;
  /** Red affirmative, and focus starts on Cancel. */
  destructive?: boolean;
};

/** The same dialog with one field: what `window.prompt` was used for. */
export type TextOptions = ConfirmOptions & {
  /** The field's visible label. */
  label: string;
  defaultValue?: string;
  placeholder?: string;
  /** The affirmative stays disabled until something other than blanks is typed. */
  required?: boolean;
  /** A textarea instead of an input, for a sentence rather than a name. */
  multiline?: boolean;
};

type Request =
  | { kind: "confirm"; opts: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: "text"; opts: TextOptions; resolve: (value: string | null) => void };

type Dialogs = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  askText: (opts: TextOptions) => Promise<string | null>;
};

const Ctx = createContext<Dialogs | null>(null);

function useDialogs(): Dialogs {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("ConfirmProvider is missing above this component");
  return ctx;
}

/** `const confirm = useConfirm()` … `if (await confirm({…})) act()` — the shape the call
 * sites had with `window.confirm`, one `await` longer. */
export function useConfirm(): Dialogs["confirm"] {
  return useDialogs().confirm;
}

/** `const askText = useAskText()` … resolves to the text, or to null when cancelled. */
export function useAskText(): Dialogs["askText"] {
  return useDialogs().askText;
}

function answer(request: Request, value: string | boolean | null): void {
  if (request.kind === "confirm") request.resolve(value === true || typeof value === "string");
  else request.resolve(typeof value === "string" ? value : null);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  // The open request also lives in a ref, so replacing one dialog with another can still
  // answer the first: a promise nobody resolves hangs its caller for ever.
  const open = useRef<Request | null>(null);

  const put = useCallback((next: Request) => {
    const previous = open.current;
    if (previous) answer(previous, null);
    open.current = next;
    setRequest(next);
  }, []);

  const api = useMemo<Dialogs>(
    () => ({
      confirm: (opts) => new Promise<boolean>((resolve) => put({ kind: "confirm", opts, resolve })),
      askText: (opts) => new Promise<string | null>((resolve) => put({ kind: "text", opts, resolve })),
    }),
    [put],
  );

  const close = useCallback((value: string | boolean | null) => {
    const current = open.current;
    open.current = null;
    setRequest(null);
    if (current) answer(current, value);
  }, []);

  return (
    <Ctx.Provider value={api}>
      {children}
      {request && <Dialog key={idOf(request)} request={request} onClose={close} />}
    </Ctx.Provider>
  );
}

let counter = 0;
const ids = new WeakMap<Request, number>();
/** A fresh key per request, so the next dialog starts with a fresh field and a fresh mount
 * (the focus effect below runs once per dialog, not once per render). */
function idOf(request: Request): number {
  let id = ids.get(request);
  if (id === undefined) {
    id = ++counter;
    ids.set(request, id);
  }
  return id;
}

function Dialog({
  request,
  onClose,
}: {
  request: Request;
  onClose: (value: string | boolean | null) => void;
}) {
  const { t } = useTranslation();
  const opts = request.opts;
  const text = request.kind === "text" ? request.opts : null;
  const [value, setValue] = useState(text?.defaultValue ?? "");
  const fieldId = useId();

  /** The field first — it is what the caller opened the dialog to fill in, and like
   * `window.prompt` its suggested value is selected so typing replaces it. Otherwise Cancel
   * when the affirmative is destructive, so a hurried Return keeps the data. */
  const initialFocus = (box: HTMLElement) => {
    const field = box.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
    if (field) {
      field.focus();
      field.select();
      return field;
    }
    const buttons = box.querySelectorAll<HTMLButtonElement>("button:not([disabled])");
    return buttons[opts.destructive ? 0 : buttons.length - 1];
  };

  const blocked = !!text?.required && value.trim() === "";
  function submit(e: FormEvent) {
    e.preventDefault();
    if (blocked) return;
    onClose(text ? value : true);
  }

  return (
    <Sheet title={opts.title} lead={opts.body} onClose={() => onClose(null)} initialFocus={initialFocus}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        {text && (
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={fieldId}>{text.label}</label>
            {text.multiline ? (
              <textarea
                id={fieldId}
                className="field w-full"
                autoComplete="off"
                rows={3}
                value={value}
                placeholder={text.placeholder}
                onChange={(e) => setValue(e.target.value)}
              />
            ) : (
              <input
                id={fieldId}
                className="field w-full"
                autoComplete="off"
                spellCheck={false}
                value={value}
                placeholder={text.placeholder}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
          </div>
        )}
        <div className="mt-2 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => onClose(null)}>
            {opts.cancelLabel ?? t("dialog.cancel")}
          </Button>
          <Button variant={opts.destructive ? "destructive" : "primary"} type="submit" disabled={blocked}>
            {opts.confirmLabel}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
